// NetworkConfig.tsx
import { useState, useCallback, useEffect } from "react";
import { Loader2, Info, Server, Clock, Users } from "lucide-react";
import { showError } from "../../../../../utils/toast";
import baseapi from "../../../../../services/baseapi";

// ── Types ─────────────────────────────────────────────────────────────────────
interface DnsData {
    log: string;
    retry: number;
    domain: { domain: string }[];
    primary: string;
    timeout: number;
    protocol: string;
    secondary: string;
    "dns-cache-ttl": number;
    "dns-cache-limit": number;
    "ssl-certificate": string;
    "server-select-method": string;
    "interface-select-method": string;
    "cache-notfound-responses": string;
    [key: string]: any;
}

interface NtpData {
    type: string;
    ntpsync: string;
    "key-type": string;
    interface: { "interface-name": string }[];
    "source-ip": string;
    "server-mode": string;
    syncinterval: number;
    authentication: string;
    [key: string]: any;
}

interface LdapItem {
    dn: string;
    cnid: string;
    name: string;
    port: number;
    type: string;
    secure: string;
    server: string;
    username: string;
    "two-factor": string;
    "obtain-user-info": string;
    antiphish: string;
    [key: string]: any;
}

// ── Value Display ─────────────────────────────────────────────────────────────
const Val = ({ val }: { val: string | number | undefined }) => {
    const str = String(val ?? "");
    if (!str || str === "0.0.0.0" || str === "::" || str === "" || str === "0")
        return <span className="text-slate-400 dark:text-gray-500 text-[11px]">—</span>;

    const lower = str.toLowerCase();
    const chip =
        lower === "enable" || lower === "enabled"
            ? "bg-green-500/10 dark:bg-green-500/15 border border-green-500/20 dark:border-green-500/30 text-green-600 dark:text-green-400"
            : lower === "disable" || lower === "disabled"
                ? "bg-orange-500/10 dark:bg-orange-500/15 border border-orange-500/20 dark:border-orange-500/30 text-orange-600 dark:text-orange-400"
                : lower === "cleartext"
                    ? "bg-blue-500/10 dark:bg-blue-500/15 border border-blue-500/20 dark:border-blue-500/30 text-blue-600 dark:text-blue-300"
                    : null;

    if (chip) {
        return (
            <span className={`inline-flex items-center px-2 py-0.5 rounded text-[10px] font-bold uppercase whitespace-nowrap ${chip}`}>
                {lower === "enable" ? "Enabled" : lower === "disable" ? "Disabled" : str}
            </span>
        );
    }

    return <span className="text-[11px] text-slate-800 dark:text-gray-200 font-mono text-right">{str}</span>;
};

// ── Chip for LDAP secure field ────────────────────────────────────────────────
const SecureChip = ({ val }: { val: string }) => {
    const lower = val?.toLowerCase() ?? "";
    if (lower === "disable" || !val) {
        return (
            <span className="inline-flex items-center px-2 py-0.5 rounded text-[10px] font-bold bg-orange-500/10 dark:bg-orange-500/15 border border-orange-500/20 dark:border-orange-500/30 text-orange-600 dark:text-orange-400">
                Disabled (plaintext)
            </span>
        );
    }
    return (
        <span className="inline-flex items-center px-2 py-0.5 rounded text-[10px] font-bold bg-green-500/10 dark:bg-green-500/15 border border-green-500/20 dark:border-green-500/30 text-green-600 dark:text-green-400 uppercase">
            {val}
        </span>
    );
};

// ── Row ───────────────────────────────────────────────────────────────────────
const Row = ({ label, children }: { label: string; children: React.ReactNode }) => (
    <div className="flex items-center justify-between py-1.5 border-b border-slate-100 dark:border-gray-700/60 last:border-0">
        <span className="text-[11px] text-slate-600 dark:text-gray-300 shrink-0">{label}</span>
        <div className="ml-4 text-right">{children}</div>
    </div>
);

// ── Card ──────────────────────────────────────────────────────────────────────
const Card = ({ icon: Icon, title, iconColor, children }: {
    icon: any; title: string; iconColor: string; children: React.ReactNode;
}) => (
    <div className="bg-white dark:bg-gray-800/60 border border-slate-200 dark:border-gray-700 rounded-xl overflow-hidden flex flex-col shadow-sm dark:shadow-none">
        <div className="px-5 py-3.5 border-b border-slate-100 dark:border-gray-700/60 bg-slate-50/50 dark:bg-transparent">
            <div className="flex items-center gap-2">
                <Icon size={14} className={iconColor} />
                <h3 className="text-[13px] font-bold text-slate-900 dark:text-white">{title}</h3>
            </div>
        </div>
        <div className="px-5 py-2 flex-1">{children}</div>
    </div>
);

// ── Main Component ────────────────────────────────────────────────────────────
export default function NetworkConfig({ asset_id, client_id }: { asset_id: any, client_id: string }) {
    const [dns, setDns] = useState<DnsData | null>(null);
    const [ntp, setNtp] = useState<NtpData | null>(null);
    const [ldap, setLdap] = useState<LdapItem[]>([]);
    const [loading, setLoading] = useState(true);

    const fetchAll = useCallback(async () => {
        setLoading(true);
        try {
            const sections = ["dns_servers", "ntp_servers", "ldap_servers"];

            const res = await baseapi.get("/firewall_sections", {
                params: {
                    query: JSON.stringify({
                        ...(asset_id && client_id ? { asset_id, client_id } : {}),
                        section_name: { $in: sections }
                    }),
                    limit: 3,
                },
            });

            if (res.data.status && res.data.data) {
                res.data.data.forEach((item: any) => {
                    const raw = item.results;
                    const parsed = typeof raw === "string" ? JSON.parse(raw) : raw;

                    if (item.section_name === "dns_servers") {
                        setDns(parsed);
                    }
                    if (item.section_name === "ntp_servers") {
                        setNtp(parsed);
                    }
                    if (item.section_name === "ldap_servers") {
                        setLdap(parsed.items ?? []);
                    }
                });
            }

        } catch (e: any) {
            showError(e.message);
        } finally {
            setLoading(false);
        }
    }, [asset_id, client_id]);

    useEffect(() => { fetchAll(); }, [fetchAll]);

    if (loading) return (
        <div className="flex items-center justify-center gap-2 py-20">
            <Loader2 className="w-4 h-4 animate-spin text-blue-600 dark:text-blue-400" />
            <span className="text-[10px] font-bold uppercase tracking-widest text-slate-500 dark:text-gray-500">Loading network configuration...</span>
        </div>
    );

    const ldapItem = ldap[0] ?? null;

    return (
        <div className="space-y-4">

            {/* 3-Column Cards */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">

                {/* ── DNS Servers ── */}
                <Card icon={Server} title="DNS servers" iconColor="text-blue-600 dark:text-blue-400">
                    {dns ? (
                        <>
                            <Row label="Primary"><Val val={dns.primary} /></Row>
                            <Row label="Secondary"><Val val={dns.secondary} /></Row>
                            <Row label="Protocol">
                                <span className="inline-flex items-center px-2 py-0.5 rounded text-[10px] font-bold bg-blue-500/10 dark:bg-blue-500/15 border border-blue-500/20 dark:border-blue-500/30 text-blue-600 dark:text-blue-300 uppercase">
                                    {dns.protocol || "-"}
                                </span>
                            </Row>
                            <Row label="SSL cert"><Val val={dns["ssl-certificate"]} /></Row>
                            <Row label="Cache limit"><Val val={dns["dns-cache-limit"]?.toLocaleString()} /></Row>
                            <Row label="Cache TTL">
                                {dns["dns-cache-ttl"] ? (
                                    <span className="text-[11px] text-slate-800 dark:text-gray-200 font-mono">{dns["dns-cache-ttl"]} s</span>
                                ) : (
                                    <span className="text-[11px] text-slate-800 dark:text-gray-200 font-mono">-</span>
                                )}
                            </Row>
                            <Row label="Timeout">
                                {dns.timeout ? (
                                    <span className="text-[11px] text-slate-800 dark:text-gray-200 font-mono">
                                        {dns.timeout} s · retry {dns.retry}
                                    </span>
                                ) : (
                                    <span className="text-[11px] text-slate-800 dark:text-gray-200 font-mono">-</span>
                                )}
                            </Row>
                            <Row label="Search domain">
                                <Val val={dns.domain?.[0]?.domain} />
                            </Row>
                            <Row label="Log"><Val val={dns.log} /></Row>
                        </>
                    ) : (
                        <div className="py-10 flex flex-col items-center gap-2 opacity-40">
                            <Info size={16} className="text-slate-400 dark:text-gray-500" />
                            <span className="text-[10px] text-slate-500 dark:text-gray-500">No DNS data</span>
                        </div>
                    )}
                </Card>

                {/* ── NTP Configuration ── */}
                <Card icon={Clock} title="NTP configuration" iconColor="text-purple-600 dark:text-purple-400">
                    {ntp ? (
                        <>
                            <Row label="NTP sync"><Val val={ntp.ntpsync} /></Row>
                            <Row label="Type">
                                <span className="text-[11px] text-slate-800 dark:text-gray-200 font-mono capitalize">
                                    {ntp.type === "fortiguard" ? "FortiGuard" : ntp.type}
                                </span>
                            </Row>
                            <Row label="Sync interval">
                                {ntp.syncinterval ? (
                                    <span className="text-[11px] text-slate-800 dark:text-gray-200 font-mono">{ntp.syncinterval} min</span>
                                ) : (
                                    <span className="text-[11px] text-slate-800 dark:text-gray-200 font-mono">-</span>
                                )}
                            </Row>
                            <Row label="Server mode"><Val val={ntp["server-mode"]} /></Row>
                            <Row label="Authentication"><Val val={ntp.authentication} /></Row>
                            <Row label="Interface">
                                <Val val={ntp.interface?.[0]?.["interface-name"]} />
                            </Row>
                            <Row label="Source IP"><Val val={ntp["source-ip"]} /></Row>
                            <Row label="Key type">
                                <span className="text-[11px] text-slate-800 dark:text-gray-200 font-mono">{ntp["key-type"] || "—"}</span>
                            </Row>
                        </>
                    ) : (
                        <div className="py-10 flex flex-col items-center gap-2 opacity-40">
                            <Info size={16} className="text-slate-400 dark:text-gray-500" />
                            <span className="text-[10px] text-slate-500 dark:text-gray-500">No NTP data</span>
                        </div>
                    )}
                </Card>

                {/* ── LDAP Server ── */}
                <Card icon={Users} title="LDAP server" iconColor="text-green-600 dark:text-green-400">
                    {ldapItem ? (
                        <>
                            <Row label="Name">
                                <span className="text-[11px] text-slate-800 dark:text-gray-200 font-mono font-bold">{ldapItem.name}</span>
                            </Row>
                            <Row label="Server">
                                <span className="text-[11px] text-slate-800 dark:text-gray-200 font-mono">
                                    {ldapItem.server}:{ldapItem.port}
                                </span>
                            </Row>
                            <Row label="Base DN">
                                <span className="text-[11px] text-slate-800 dark:text-gray-200 font-mono">{ldapItem.dn}</span>
                            </Row>
                            <Row label="Username">
                                <span className="text-[11px] text-slate-800 dark:text-gray-200 font-mono">{ldapItem.username}</span>
                            </Row>
                            <Row label="Bind type">
                                <span className="text-[11px] text-slate-800 dark:text-gray-200 font-mono">{ldapItem.type}</span>
                            </Row>
                            <Row label="CNID">
                                <span className="text-[11px] text-slate-800 dark:text-gray-200 font-mono">{ldapItem.cnid}</span>
                            </Row>
                            <Row label="Secure"><SecureChip val={ldapItem.secure} /></Row>
                            <Row label="2FA"><Val val={ldapItem["two-factor"]} /></Row>
                            <Row label="Obtain user info"><Val val={ldapItem["obtain-user-info"]} /></Row>
                        </>
                    ) : (
                        <div className="py-10 flex flex-col items-center gap-2 opacity-40">
                            <Info size={16} className="text-slate-400 dark:text-gray-300" />
                            <span className="text-[10px] text-slate-500 dark:text-gray-300">No LDAP data found</span>
                        </div>
                    )}
                </Card>
            </div>

            {/* Extra LDAP servers if more than 1 */}
            {ldap.length > 1 && (
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                    {ldap.slice(1).map(item => (
                        <Card key={item.name} icon={Users} title={`LDAP · ${item.name}`} iconColor="text-green-600 dark:text-green-400">
                            <Row label="Server"><span className="text-[11px] text-slate-800 dark:text-gray-200 font-mono">{item.server}:{item.port}</span></Row>
                            <Row label="Base DN"><span className="text-[11px] text-slate-800 dark:text-gray-200 font-mono">{item.dn}</span></Row>
                            <Row label="Username"><span className="text-[11px] text-slate-800 dark:text-gray-200 font-mono">{item.username}</span></Row>
                            <Row label="Bind type"><span className="text-[11px] text-slate-800 dark:text-gray-200 font-mono">{item.type}</span></Row>
                            <Row label="CNID"><span className="text-[11px] text-slate-800 dark:text-gray-200 font-mono">{item.cnid}</span></Row>
                            <Row label="Secure"><SecureChip val={item.secure} /></Row>
                            <Row label="2FA"><Val val={item["two-factor"]} /></Row>
                            <Row label="Obtain user info"><Val val={item["obtain-user-info"]} /></Row>
                        </Card>
                    ))}
                </div>
            )}
        </div>
    );
}
